import React from "react";
import PropTypes from "prop-types";
import { Badge, Button, LegacyCard as Card, Text, EmptyState, Layout, InlineStack } from "@shopify/polaris";
import { useNavigate, useLocation } from "react-router-dom";
import { PrivateConsumer } from "lib/context";
import { featureHelper, statusHelper } from "lib/helpers";
import constants from "lib/constant/constant";

const { featureError, featureVersion } = constants;
const pattern = /^((http|https|ftp):\/\/)/;

const withFeature = (WrappedComponent, featureConfig) => {
  const FeatureWrapper = React.forwardRef((props, ref) => {
    const { onValidate, ...rest } = props;
    const navigate = useNavigate();
    const location = useLocation();

    const nextPath = (path) => {
      if (pattern.test(path)) {
        window.open(path);
      } else {
        navigate(path);
      }
    };

    const renderError = ({ heading, action }, annotated) => {
      const section = annotated && (
        <Layout.AnnotatedSection title={annotated.label} description={annotated.description}>
          <Card title={annotated.label} sectioned>
            <InlineStack align="space-between" blockAlign="center">
              <Text variant="bodySm">{heading}</Text>
              <Button variant="primary" onClick={() => nextPath(action.url)}>
                {/* {action.content} */}
                Dashboard
              </Button>
            </InlineStack>
          </Card>
        </Layout.AnnotatedSection>
      );
      if (section) return section;
      return <EmptyState heading={heading} action={{ content: action.content, url: action.url }} />;
    };

    return (
      <PrivateConsumer>
        {({ oldCMS: cms = {}, currentUser, featureFlag }) => {
          const isRoot = location.pathname === "/";
          const response = featureHelper.checkFeature({
            currentUser,
            featureFlag,
            ...featureConfig,
          });

          if (onValidate) {
            onValidate(!response?.error);
          }
          if (response?.error === featureError.hideFeature && !isRoot) {
            return null;
          }
          if (response?.error && !isRoot) {
            const feature = response.feature || {};
            const { label, description } = feature;
            const cmsMsg = cms.message || {};
            const actions = {
              roleNotAllowed: {
                heading: cmsMsg.notAllowed,
                action: { content: cms.dashboard, url: "/" },
              },
              needPlanUpgrade: {
                heading: cmsMsg.upgradePlan,
                action: { content: cms.upgradePlan, url: "/update/plan" },
              },
            };
            const errorAction = actions[response.error] || {
              heading: cmsMsg.availableSoon,
              action: { content: cms.dashboard, url: "/" },
            };
            const annotated = (label && description && { label, description }) || {
              label: cmsMsg.availableSoon,
              description: "",
            };

            return renderError(errorAction, annotated);
          }

          const getSectionTitle = (title) => {
            const ver = response.feature?.version;
            if ([featureVersion.beta, featureVersion.alpha].includes(ver)) {
              return (
                <InlineStack>
                  <Text variant="bodyLg">{title}</Text>
                  <Badge status={statusHelper.getBadgeType(ver)}>{statusHelper.getBadgeStatus(ver)}</Badge>
                </InlineStack>
              );
            }
            return title;
          };

          return <WrappedComponent ref={ref} {...rest} getSectionTitle={getSectionTitle} isFeatureValid />;
        }}
      </PrivateConsumer>
    );
  });

  FeatureWrapper.propTypes = {
    onValidate: PropTypes.func,
  };
  FeatureWrapper.defaultProps = {
    onValidate: undefined,
  };

  return FeatureWrapper;
};

export default withFeature;
